import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { RequiredAuth, RequiredReg, RequiredMarketingFlow } from "./utils";
import { LinearProgress } from "@mui/material";
import React, { Suspense, useEffect, useRef } from "react";
import { LoadingPage } from "../components/LoadingPage";
import {
  updateCurrentPlan,
  updateSubscriptions,
  failedToCheckSubscriptions,
} from "../redux/slices/consumerSubscriptionSlice";
import { useAppDispatch, useAxios } from "../utils/hooks";
import { useCookies } from "react-cookie";
import {
  API_BASE_URL,
  MARKETING_ATTRIBUTES_COOKIES_KEY_NAME,
} from "../utils/constants";
import { parsePlanFromKeycloak } from "../utils/isExtensionPremium";
import useCeMetrics from "../metrics/useCeMetrics";
import MiddleHandler from "./MiddleHandler";
import {
  EXTENSION_UNINSTALLATION_PAGE_ENDPOINT,
  HOME_PAGE_ENDPOINT,
  INTELLIWRITER_AUTH_ENDPOINT,
  INSTITUTIONAL_SUBSCRIPTION_PAGE_ENDPOINT,
  MIDDLE_HANDLER_ENDPOINT,
  PLAN_PAGE_ENDPOINT,
  PLAN_RESULT_PAGE_ENDPOINT,
  PRODUCTS_PAGE_ENDPOINT,
  REPORT_LIST_PAGE_ENDPOINT,
  REPORT_VIEW_PAGE_ENDPOINT,
  RESOURCE_CONTENT_ENDPOINT,
  ARTIFACT_CONTENT_ENDPOINT,
  SETTINGS_PAGE_ENDPOINT,
  USER_RUBRICS_PAGE_ENDPOINT,
  WELCOME_PAGE_ENDPOINT,
} from "./endpointConstants";
import { getCeSharedSubDomain } from "../utils/utils";
import { AdminRoutes } from "./AdminRoutes";
import ProductsPage from "../components/products/Products";
import ContentDownloadPage from "../components/report/ContentDownloadPage";

// Lazy load components

// Core
const SettingPage = React.lazy(() => import("../components/settings/Settings"));

// Marketing & Onboarding
const WelcomePage = React.lazy(
  () => import("../components/onboarding/WelcomePage"),
);
const PricingPage = React.lazy(
  () => import("../components/marketing/PricingPage"),
);
const StripePaymentResultPage = React.lazy(
  () => import("../components/marketing/stripe/StripePaymentResultPage"),
);
const InstitutionalSubscriptionPage = React.lazy(
  () => import("../components/marketing/stripe/InstitutionalSubscriptionPage"),
);
const ExtensionUninstallationGoodbyePage = React.lazy(
  () => import("../components/ExtensionUninstallationGoodbyePage"),
);
const IntelliWriterAuthPage = React.lazy(
  () => import("../components/IntelliWriterAuthPage"),
);

// Reports
const ReportPage = React.lazy(() => import("../components/report/ReportPage"));
const ReportDisplay = React.lazy(
  () => import("../components/report/ReportDisplay"),
);
const ReportCreatePage = React.lazy(
  () => import("../components/report/ReportCreatePage"),
);
const ResourceDownloadPage = React.lazy(
  () => import("../components/report/ContentDownloadPage"),
);

// Rubrics
const UserRubricPage = React.lazy(
  () => import("../components/user-rubrics/UserRubricPage"),
);
const UserRubricEditPage = React.lazy(
  () => import("../components/user-rubrics/UserRubricEditPage"),
);

export const AppRouter = () => {
  const { initialized, keycloak } = useKeycloak();
  const hasPrefetched = useRef(false);

  const {
    readTrackSessionToken,
    setTrackSessionToken,
    sendMarketingParamsToApiWithAuth,
  } = useCeMetrics(keycloak);

  const dispatch = useAppDispatch();

  const axios = useAxios(API_BASE_URL, keycloak);
  const [cookies, , removeCookie] = useCookies([
    MARKETING_ATTRIBUTES_COOKIES_KEY_NAME,
  ]);

  useEffect(() => {
    if (initialized && !!keycloak) {
      dispatch(updateCurrentPlan(parsePlanFromKeycloak(keycloak)));

      fetch(API_BASE_URL + "/subscription", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${keycloak.token}`,
        },
      })
        .then((r) => r.json())
        .then((data) => {
          dispatch(updateSubscriptions(data));
          keycloak.updateToken(300).then((refreshed) => {
            if (refreshed) {
              dispatch(updateCurrentPlan(parsePlanFromKeycloak(keycloak)));
            }
          });
        })
        .catch((e) => dispatch(failedToCheckSubscriptions()));

      !!cookies[MARKETING_ATTRIBUTES_COOKIES_KEY_NAME] &&
        sendMarketingParamsToApiWithAuth(
          [
            {
              type: "marketing_params_sync",
              data: cookies[MARKETING_ATTRIBUTES_COOKIES_KEY_NAME],
            },
          ],
          () => {
            const parsedDomain = getCeSharedSubDomain();
            removeCookie(MARKETING_ATTRIBUTES_COOKIES_KEY_NAME, {
              domain: parsedDomain,
            });
          },
        );

      // request track session token
      const existingTrackSessionToken = readTrackSessionToken();
      // TODO we can add authenticated check here to determine if we need to re-get a token
      if (typeof existingTrackSessionToken !== "string") {
        fetch(API_BASE_URL + "/track/session/token", {
          method: "GET",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((r) => r?.text())
          .then((t) => setTrackSessionToken(t))
          .catch((error) => {
            console.warn("Failed to fetch track session token:", error);
          });
      }

      // Prefetch commonly accessed routes when browser is idle (only once)
      if (keycloak.authenticated && !hasPrefetched.current) {
        hasPrefetched.current = true;

        const prefetch = (importFn: () => Promise<any>) =>
          importFn().catch(() => {});

        const prefetchRoutes = () => {
          prefetch(() => import("../components/settings/Settings"));
          prefetch(() => import("../components/report/ReportPage"));
          prefetch(() => import("../components/report/ReportDisplay"));
          prefetch(() => import("../components/report/ReportCreatePage"));
          prefetch(() => import("../components/user-rubrics/UserRubricPage"));
          prefetch(
            () => import("../components/user-rubrics/UserRubricEditPage"),
          );
          prefetch(() => import("../components/report/ContentDownloadPage"));
        };

        if ("requestIdleCallback" in window) {
          const id = requestIdleCallback(prefetchRoutes, { timeout: 5000 });
          return () => cancelIdleCallback(id);
        } else {
          // Fallback for browsers without requestIdleCallback (Safari)
          const timeout = setTimeout(prefetchRoutes, 3000);
          return () => clearTimeout(timeout);
        }
      }

      // send out the event, if applicable | deprecated, but keep it for potential usage
      // const trackSessionToken = readTrackSessionToken();
      // const trackEvent = readTrackEventFromCookies();
      // if ((typeof trackSessionToken === 'string')
      //     && typeof trackEvent !== 'undefined'
      //     && (typeof trackEvent === 'object')
      //     && trackEvent !== null
      //     // && trackEvent['attempt'] !== true
      // ) {
      //     sendEventToApiWithoutAuth(trackSessionToken, [(!!cookies && !!trackEvent)? Object.assign(trackEvent, cookies) : trackEvent])?.then(
      //         r => {
      //             if (r?.status === 200) {
      //                 //TODO enable the removals
      //                 // alert("send event successfully: " + JSON.stringify(!!cookies? Object.assign(trackEvent, cookies) : trackEvent))
      //                 // @ts-ignore
      //                 // removeTrackEventCookies();
      //                 // @ts-ignore
      //                 // removeTrackSessionTokenCookies();
      //             }
      //         }
      //     );
      // }
    }
  }, [initialized, keycloak]);

  if (!initialized) {
    return (
      <LinearProgress
        sx={{ "& .MuiLinearProgress-bar": { backgroundColor: "#6095c6" } }}
      />
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path={INTELLIWRITER_AUTH_ENDPOINT}
          element={
            <Suspense fallback={<LoadingPage title="Authenticating" />}>
              <IntelliWriterAuthPage />
            </Suspense>
          }
        />

        {/* Marketing & Onboarding */}
        <Route
          path={WELCOME_PAGE_ENDPOINT + "/*"}
          element={
            <RequiredMarketingFlow>
              <Suspense
                fallback={
                  <LinearProgress
                    sx={{
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#6095c6",
                      },
                    }}
                  />
                }
              >
                <WelcomePage />
              </Suspense>
            </RequiredMarketingFlow>
          }
        />
        <Route
          path={EXTENSION_UNINSTALLATION_PAGE_ENDPOINT}
          element={
            <Suspense
              fallback={
                <LinearProgress
                  sx={{
                    "& .MuiLinearProgress-bar": { backgroundColor: "#6095c6" },
                  }}
                />
              }
            >
              <ExtensionUninstallationGoodbyePage />
            </Suspense>
          }
        />

        {/* Core Pages */}
        <Route path={MIDDLE_HANDLER_ENDPOINT} element={<MiddleHandler />} />
        <Route
          path={HOME_PAGE_ENDPOINT}
          element={
            <RequiredAuth>
              <Suspense fallback={<LoadingPage title="Home" />}>
                <ProductsPage />
              </Suspense>
            </RequiredAuth>
          }
        />
        <Route
          path={PRODUCTS_PAGE_ENDPOINT}
          element={
            <RequiredAuth>
              <Suspense fallback={<LoadingPage title="Home" />}>
                <ProductsPage />
              </Suspense>
            </RequiredAuth>
          }
        />
        <Route
          path={SETTINGS_PAGE_ENDPOINT}
          element={
            <RequiredAuth>
              <Suspense fallback={<LoadingPage title="Settings" />}>
                <SettingPage />
              </Suspense>
            </RequiredAuth>
          }
        />

        {/* Plans & Subscriptions */}
        <Route
          path={PLAN_PAGE_ENDPOINT}
          element={
            <RequiredReg>
              <Suspense
                fallback={
                  <LinearProgress
                    sx={{
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#6095c6",
                      },
                    }}
                  />
                }
              >
                <PricingPage />
              </Suspense>
            </RequiredReg>
          }
        />
        <Route
          path={PLAN_RESULT_PAGE_ENDPOINT + "/*"}
          element={
            <RequiredAuth>
              <Suspense fallback={<LoadingPage title="Payment Result" />}>
                <StripePaymentResultPage />
              </Suspense>
            </RequiredAuth>
          }
        />
        <Route
          path={INSTITUTIONAL_SUBSCRIPTION_PAGE_ENDPOINT}
          element={
            <RequiredAuth>
              <Suspense
                fallback={<LoadingPage title="Institutional Subscription" />}
              >
                <InstitutionalSubscriptionPage />
              </Suspense>
            </RequiredAuth>
          }
        />

        {/* Reports */}
        <Route
          path={REPORT_LIST_PAGE_ENDPOINT}
          element={
            <RequiredAuth>
              <Suspense fallback={<LoadingPage title="Reports" />}>
                <ReportPage />
              </Suspense>
            </RequiredAuth>
          }
        />
        <Route
          path="/reports/create"
          element={
            <RequiredAuth>
              <Suspense fallback={<LoadingPage title="Create Report" />}>
                <ReportCreatePage />
              </Suspense>
            </RequiredAuth>
          }
        />
        <Route
          path={REPORT_VIEW_PAGE_ENDPOINT}
          element={
            <RequiredAuth>
              <Suspense fallback={<LoadingPage title="Report" />}>
                <ReportDisplay />
              </Suspense>
            </RequiredAuth>
          }
        />
        <Route
          path={RESOURCE_CONTENT_ENDPOINT}
          element={
            <RequiredAuth>
              <Suspense fallback={<LoadingPage title="Resource" />}>
                <ContentDownloadPage />
              </Suspense>
            </RequiredAuth>
          }
        />

        <Route
          path={ARTIFACT_CONTENT_ENDPOINT}
          element={
            <RequiredAuth>
              <Suspense fallback={<LoadingPage title="Artifact" />}>
                <ContentDownloadPage />
              </Suspense>
            </RequiredAuth>
          }
        />

        {/* Rubrics */}
        <Route
          path={USER_RUBRICS_PAGE_ENDPOINT}
          element={
            <RequiredAuth>
              <Suspense fallback={<LoadingPage title="My Rubrics" />}>
                <UserRubricPage />
              </Suspense>
            </RequiredAuth>
          }
        />
        <Route
          path="my-rubrics/:rubricId/update/edit"
          element={
            <RequiredAuth>
              <Suspense fallback={<LoadingPage title="Edit Rubric" />}>
                <UserRubricEditPage />
              </Suspense>
            </RequiredAuth>
          }
        />
        <Route
          path="my-rubrics/:rubricId/create/edit"
          element={
            <RequiredAuth>
              <Suspense fallback={<LoadingPage title="Create Rubric" />}>
                <UserRubricEditPage />
              </Suspense>
            </RequiredAuth>
          }
        />
        <Route
          path="my-rubrics/:rubricId/view"
          element={
            <RequiredAuth>
              <Suspense fallback={<LoadingPage title="View Rubric" />}>
                <UserRubricEditPage />
              </Suspense>
            </RequiredAuth>
          }
        />

        {/* Admin */}
        <Route
          path="/admin/*"
          element={
            <Suspense fallback={<LoadingPage title="Admin" />}>
              <AdminRoutes />
            </Suspense>
          }
        />

        {/* Fallback */}
        <Route
          path="/*"
          element={
            <RequiredAuth>
              <Suspense fallback={<LoadingPage title="Home" />}>
                <ProductsPage />
              </Suspense>
            </RequiredAuth>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};
